import { DEFAULT_CONCURRENCY, MAX_RETRIES, BASE_RETRY_DELAY_MS } from "../constants";

// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const putChunkXHR = (presignedUrl, chunk, onChunkProgress, signal) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onChunkProgress(e.loaded / e.total);
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const rawEtag = xhr.getResponseHeader("ETag");
        if (!rawEtag) {
          reject(new Error("S3 did not return an ETag for this part."));
          return;
        }
        resolve(rawEtag.replace(/"/g, ""));
      } else {
        reject(new Error(`S3 returned HTTP ${xhr.status} for part upload.`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error during part upload.")));
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled.")));

    if (signal) {
      if (signal.aborted) {
        reject(new Error("Upload cancelled."));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.open("PUT", presignedUrl);
    xhr.send(chunk);
  });

const uploadPartWithRetry = async (
  { partNumber, presignedUrl, chunk },
  onChunkProgress,
  signal
) => {
  let lastError;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new Error("Upload cancelled.");

    try {
      const etag = await putChunkXHR(presignedUrl, chunk, onChunkProgress, signal);
      return { partNumber, etag };
    } catch (err) {
      lastError = err;

      if (err.message === "Upload cancelled." || signal?.aborted) {
        throw new Error("Upload cancelled.");
      }

      if (attempt < MAX_RETRIES) {
        const delay = BASE_RETRY_DELAY_MS * attempt;
        console.warn(
          `[multipartUpload] Part ${partNumber} attempt ${attempt} failed ` +
            `("${err.message}"). Retrying in ${delay}ms…`
        );
        await sleep(delay);
      }
    }
  }

  throw new Error(`Part ${partNumber} failed after ${MAX_RETRIES} attempts: ${lastError?.message}`);
};

// Public API
// ─────────────────────────────────────────────────────────────────────────────

export const uploadPartsWithConcurrency = async ({
  videoFile,
  partUrls,
  partSize,
  onProgress = () => {},
  signal,
  concurrency = DEFAULT_CONCURRENCY,
}) => {
  const totalParts = partUrls.length;
  const partProgress = new Array(totalParts).fill(0); // fraction 0–1 per part
  const completed = []; // { partNumber, etag }

  // Recalculate and emit overall progress whenever any part reports progress
  const tick = () => {
    const sum = partProgress.reduce((acc, p) => acc + p, 0);
    onProgress(Math.min(99, Math.round((sum / totalParts) * 100)));
  };

  // Build the parts queue
  const queue = partUrls.map((presignedUrl, i) => {
    const partNumber = i + 1;
    const start = i * partSize;
    const end = Math.min(start + partSize, videoFile.size);
    return { partNumber, presignedUrl, chunk: videoFile.slice(start, end) };
  });

  // Run N concurrent workers, each pulling from the shared queue
  await Promise.all(
    Array.from({ length: Math.min(concurrency, totalParts) }, async () => {
      while (queue.length > 0) {
        const part = queue.shift();
        if (!part) break;

        // Guard: presignedUrl must be a non-empty string.
        // If it is an object (Lambda returned wrong shape) throw clearly.
        if (
          !part.presignedUrl ||
          typeof part.presignedUrl !== "string" ||
          !part.presignedUrl.startsWith("http")
        ) {
          throw new Error(
            `Part ${part.partNumber} has an invalid presigned URL: "${part.presignedUrl}". ` +
              `Expected a string starting with "https://". ` +
              `Check that create_upload Lambda returns partUrls as plain URL strings.`
          );
        }

        const idx = part.partNumber - 1;
        const result = await uploadPartWithRetry(
          part,
          (fraction) => {
            partProgress[idx] = fraction;
            tick();
          },
          signal
        );

        partProgress[idx] = 1;
        tick();
        completed.push(result);
      }
    })
  );

  onProgress(100);
  return completed.sort((a, b) => a.partNumber - b.partNumber);
};

export const uploadSrtToS3 = (srtFile, presignedUrl, onProgress = () => {}, signal) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`SRT upload failed — S3 returned HTTP ${xhr.status}.`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error during SRT upload.")));
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled.")));

    if (signal) {
      if (signal.aborted) {
        reject(new Error("Upload cancelled."));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", srtFile.type || "text/plain");
    xhr.send(srtFile);
  });
